import { supabase } from '@/lib/supabaseClient';

export interface KnowledgeSourceSettings {
  source_type: 'app_faq' | 'sales_training' | 'custom_docs' | 'crm_data';
  is_enabled: boolean;
  configuration?: Record<string, any>;
}

export interface CRMDataSettings {
  projects_enabled: boolean;
  tickets_enabled: boolean;
  tasks_enabled: boolean;
}

class KnowledgeSourceSettingsService {
  /**
   * Get the current settings for a knowledge source
   */
  async getSourceSettings(sourceType: string): Promise<KnowledgeSourceSettings | null> {
    try {
      const { data, error } = await supabase
        .from('knowledge_sources')
        .select('source_type, is_enabled, configuration')
        .eq('source_type', sourceType)
        .single();

      if (error) {
        console.error('Error fetching knowledge source settings:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getSourceSettings:', error);
      return null;
    }
  }

  /**
   * Update the settings for a knowledge source
   */
  async updateSourceSettings(
    sourceType: string,
    isEnabled: boolean,
    configuration?: Record<string, any>
  ): Promise<boolean> {
    try {
      const updateData: any = {
        is_enabled: isEnabled,
        updated_at: new Date().toISOString()
      };

      if (configuration !== undefined) {
        updateData.configuration = configuration;
      }

      const { error } = await supabase
        .from('knowledge_sources')
        .update(updateData)
        .eq('source_type', sourceType);

      if (error) {
        console.error('Error updating knowledge source settings:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateSourceSettings:', error);
      return false;
    }
  }

  /**
   * Get CRM data settings specifically
   */
  async getCRMSettings(): Promise<CRMDataSettings> {
    const settings = await this.getSourceSettings('crm_data');
    
    if (!settings?.configuration) {
      // Default settings if none exist
      return {
        projects_enabled: true,
        tickets_enabled: true,
        tasks_enabled: true
      };
    }

    return {
      projects_enabled: settings.configuration.projects_enabled ?? true,
      tickets_enabled: settings.configuration.tickets_enabled ?? true,
      tasks_enabled: settings.configuration.tasks_enabled ?? true
    };
  }

  /**
   * Update CRM data settings
   */
  async updateCRMSettings(
    isEnabled: boolean,
    crmSettings: CRMDataSettings
  ): Promise<boolean> {
    return this.updateSourceSettings('crm_data', isEnabled, {
      projects_enabled: crmSettings.projects_enabled,
      tickets_enabled: crmSettings.tickets_enabled,
      tasks_enabled: crmSettings.tasks_enabled
    });
  }

  /**
   * Get all knowledge source settings
   */
  async getAllSourceSettings(): Promise<KnowledgeSourceSettings[]> {
    try {
      const { data, error } = await supabase
        .from('knowledge_sources')
        .select('source_type, is_enabled, configuration')
        .order('source_type');

      if (error) {
        console.error('Error fetching all knowledge source settings:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllSourceSettings:', error);
      return [];
    }
  }

  /**
   * Check if a specific source is enabled
   */
  async isSourceEnabled(sourceType: string): Promise<boolean> {
    const settings = await this.getSourceSettings(sourceType);
    return settings?.is_enabled ?? false;
  }
}

export const knowledgeSourceSettingsService = new KnowledgeSourceSettingsService();